/**
 * ADR-0013 — Créance-Einzug (Kundenzahlung auf customerDebts).
 *
 * Reduziert remainingDebt und speichert payment-Records als Liquid-Income-Quelle.
 * Solde / Session: collectCustomerDebtPayments → domain/finance liquid + session cashflow.
 */

function toAmount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

function normalizeMethod(paymentMethod, electronicPaymentMethod) {
  const pm = String(paymentMethod || 'cash').trim().toLowerCase();
  const provider = String(electronicPaymentMethod || '').trim().toLowerCase();
  if (pm === 'cash' || pm === 'espèces' || pm === 'especes') {
    return { paymentMethod: 'cash', electronicPaymentMethod: null, channel: 'cash' };
  }
  if (provider && provider !== 'cash') {
    return {
      paymentMethod: pm === 'electronic' || pm === 'électronique' ? provider : pm || provider,
      electronicPaymentMethod: provider,
      channel: 'electronic'
    };
  }
  if (pm && pm !== 'cash' && pm !== 'credit' && pm !== 'on_account') {
    return {
      paymentMethod: pm,
      electronicPaymentMethod: provider || pm,
      channel: 'electronic'
    };
  }
  return { paymentMethod: 'cash', electronicPaymentMethod: null, channel: 'cash' };
}

/**
 * @param {Array} [customerDebts]
 * @returns {Array<object>} flat payment rows for cashflow income
 */
export function collectCustomerDebtPayments(customerDebts = []) {
  const out = [];
  for (const debt of Array.isArray(customerDebts) ? customerDebts : []) {
    if (!debt) continue;
    const payments = Array.isArray(debt.payments) ? debt.payments : [];
    for (const p of payments) {
      if (!p || p.voided === true) continue;
      const amount = toAmount(p.amount ?? p.totalAmount);
      if (amount <= 0) continue;
      out.push({
        ...p,
        id: p.id || null,
        customerId: p.customerId ?? debt.customerId,
        customerName: p.customerName ?? debt.customerName,
        amount,
        paymentMethod: p.paymentMethod || 'cash',
        electronicPaymentMethod: p.electronicPaymentMethod || null,
        date: p.paymentDate || p.date || p.createdAt,
        paymentDate: p.paymentDate || p.date || p.createdAt,
        createdAt: p.createdAt || p.paymentDate || p.date,
        transactionType: 'customer_debt_payment'
      });
    }
  }
  return out;
}

/** Payment-timestamp for period/day bucketing (Créance-Einzug). */
export function resolveDebtPaymentInstant(payment) {
  const raw =
    payment?.paymentDate || payment?.date || payment?.createdAt || payment?.timestamp || null;
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Scope debt collections to [rangeStart, rangeEnd] inclusive (Date instances).
 * Prefer this over passing full customerDebts into period/day liquid.
 */
export function filterCustomerDebtPaymentsInRange(payments = [], rangeStart, rangeEnd) {
  const startMs =
    rangeStart instanceof Date ? rangeStart.getTime() : new Date(rangeStart || 0).getTime();
  const endMs =
    rangeEnd instanceof Date ? rangeEnd.getTime() : new Date(rangeEnd || 0).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return Array.isArray(payments) ? payments : [];
  }
  return (Array.isArray(payments) ? payments : []).filter((p) => {
    const inst = resolveDebtPaymentInstant(p);
    if (!inst) return false;
    const ms = inst.getTime();
    return ms >= startMs && ms <= endMs;
  });
}

/**
 * @param {object} args
 * @returns {{ success: boolean, error?: string, payment?: object, debtDelta?: number }}
 */
export function planCustomerDebtPayment({
  customerId,
  customerName = '',
  amount,
  paymentMethod = 'cash',
  electronicPaymentMethod = null,
  customerDebts = [],
  paidAt = null,
  workerId = null,
  workerName = null,
  actorRole = null,
  note = ''
} = {}) {
  if (customerId == null || customerId === '') {
    return { success: false, error: 'customer_id_missing' };
  }
  const amt = Math.max(0, toAmount(amount));
  if (amt <= 0) {
    return { success: false, error: 'invalid_amount' };
  }

  const debt = (Array.isArray(customerDebts) ? customerDebts : []).find(
    (d) => d && String(d.customerId) === String(customerId)
  );
  const remaining = Math.max(0, Number(debt?.remainingDebt) || 0);
  if (remaining <= 0) {
    return { success: false, error: 'no_open_debt' };
  }
  const applied = Math.min(amt, remaining);
  const method = normalizeMethod(paymentMethod, electronicPaymentMethod);
  const at = paidAt || new Date().toISOString();

  return {
    success: true,
    payment: {
      id: `cdp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      customerId,
      customerName: customerName || debt?.customerName || '',
      amount: applied,
      paymentMethod: method.paymentMethod,
      electronicPaymentMethod: method.electronicPaymentMethod,
      channel: method.channel,
      paymentDate: at,
      createdAt: at,
      workerId: workerId || null,
      workerName: workerName || null,
      actorRole: actorRole || null,
      note: String(note || '').trim(),
      transactionType: 'customer_debt_payment'
    },
    debtDelta: -applied,
    previousRemaining: remaining,
    nextRemaining: remaining - applied
  };
}

/**
 * Mutiert parsed.customerDebts: payment anhängen + remainingDebt senken.
 * @returns {{ applied: boolean, payment: object, customerId: * }|null}
 */
export function applyCustomerDebtPayment(parsed, plan) {
  if (!parsed || !plan?.success || !plan.payment || !(plan.debtDelta < 0)) {
    return null;
  }

  const payment = plan.payment;
  const customerId = payment.customerId;
  const debts = Array.isArray(parsed.customerDebts) ? [...parsed.customerDebts] : [];
  const idx = debts.findIndex((d) => d && String(d.customerId) === String(customerId));
  if (idx < 0) return null;

  const prev = debts[idx];
  const applied = Math.max(0, toAmount(payment.amount));
  const remainingDebt = Math.max(0, (Number(prev.remainingDebt) || 0) - applied);
  const paidAmount = Math.max(0, (Number(prev.paidAmount) || 0) + applied);
  const payments = Array.isArray(prev.payments) ? [...prev.payments, payment] : [payment];
  const now = payment.createdAt || new Date().toISOString();

  debts[idx] = {
    ...prev,
    remainingDebt,
    paidAmount,
    creditUsed: remainingDebt,
    creditAvailable: Math.max(0, (Number(prev.creditLimit) || 0) - remainingDebt),
    payments,
    updatedAt: now
  };
  parsed.customerDebts = debts;

  return {
    applied: true,
    payment,
    customerId,
    previousRemaining: plan.previousRemaining,
    nextRemaining: remainingDebt,
    at: now
  };
}
